import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import LoadingState from "../generic/LoadingState";
import ErrorState from "../generic/ErrorState";
import { useDeleteUserByIdMutation } from "../../Services/modules/users";
import { useToast } from "../ui/toast";
import ConfirmationCheckbox from "../DeleteUser/ConfirmationCheckbox";
import ActionButtons from "../DeleteUser/ActionButtons";

export default function ConfirmDeleteUserDialog({
  user,
  open,
  onOpenChange,
  onConfirm,
  onSuccess,
  queryArgs,
}) {
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState("");

  const { addToast } = useToast();
  const [deleteUser, { isLoading }] = useDeleteUserByIdMutation();

  const userName = useMemo(() => {
    return user.customUrl;
  }, [user?.firstName, user?.lastName]);

  const userEmail = useMemo(() => {
    return user?.email || "";
  }, [user?.email]);

  const dialogTitle = useMemo(() => "Confirmer la suppression", []);

  const dialogDescription = useMemo(() => {
    if (!user) return "";
    return (
      <>
        Voulez-vous vraiment supprimer l'utilisateur <b>{userName}</b> ?<br />
        Cette action est{" "}
        <span className="font-semibold text-red-500">irréversible</span>.
      </>
    );
  }, [userName, user]);

  const canConfirm = useMemo(() => {
    return checked && !isLoading;
  }, [checked, isLoading]);

  const loadingText = "Suppression...";
  const confirmText = "Supprimer définitivement";

  const handleCheckboxChange = useCallback((isChecked) => {
    setChecked(isChecked);
  }, []);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleConfirm = useCallback(async () => {
    if (!user || !checked || isLoading) return;

    setError("");

    try {
      const result = await deleteUser({
        uid: user.uid,
        confirmDeletion: true,
        queryArgs,
      }).unwrap();

      if (result.authDeletionError) {
        addToast(`Attention: ${result.authDeletionError}`, "warning");
      }

      if (result.warning) {
        addToast(result.warning, "info");
      }

      onConfirm && onConfirm(user, result);
      onSuccess && onSuccess(user, result);

      onOpenChange(false);
    } catch (err) {
      const errorMessage =
        err.data?.error ||
        err.error ||
        "Erreur lors de la suppression de l'utilisateur";

      setError(errorMessage);
      addToast("Erreur lors de la suppression de l'utilisateur", "error");
    }
  }, [
    user,
    checked,
    isLoading,
    deleteUser,
    addToast,
    onConfirm,
    onSuccess,
    onOpenChange,
    queryArgs,
  ]);

  const errorStateComponent = useMemo(() => {
    return error ? (
      <ErrorState
        error={error}
        title="Erreur de suppression"
        className="p-3 mb-4 text-sm text-red-500 bg-red-50 rounded"
      />
    ) : null;
  }, [error]);

  const confirmationSection = useMemo(() => {
    return (
      <ConfirmationCheckbox
        checked={checked}
        onChange={handleCheckboxChange}
        disabled={isLoading}
        userName={userName}
      />
    );
  }, [checked, handleCheckboxChange, isLoading, userName]);

  const actionButtons = useMemo(() => {
    return (
      <ActionButtons
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        canConfirm={canConfirm}
        isLoading={isLoading}
        loadingText={loadingText}
        confirmText={confirmText}
      />
    );
  }, [
    handleCancel,
    handleConfirm,
    canConfirm,
    isLoading,
    loadingText,
    confirmText,
  ]);

  const mainContent = useMemo(() => {
    if (isLoading) {
      return <LoadingState text="Suppression en cours..." />;
    }

    return (
      <>
        {errorStateComponent}
        {confirmationSection}
      </>
    );
  }, [isLoading, errorStateComponent, confirmationSection]);

  useEffect(() => {
    if (!open) {
      setChecked(false);
      setError("");
    }
  }, [open]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        {mainContent}
        {actionButtons}
      </DialogContent>
    </Dialog>
  );
}
